<template>
  <div :class="classes.content">
    <CardHeader :eventId="eventId" />
    <div :class="[classes.listCardWrapper, 'card', 'list']">
      <div :class="[classes.contentWrapper]">
        <EventName
          :class="[classes.eventName, 'event-name']"
          :eventId="eventId"
        />
        <div :class="[classes.promoWrapper]">
          <PromoFittedText
            :minSize="8"
            :alignment="'flex-start'"
            :promoText="optionText"
          />
        </div>
      </div>

      <ListOption
        :key="option.id"
        :eventId="eventId"
        :class="classes.optionButton"
        :option="option"
        :marketId="market.marketId"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, ComputedRef, defineComponent } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import CardHeader from "../../CardHeader.vue";
import ListOption from "./ListOption.vue";
import EventName from "../../EventName.vue";
import PromoFittedText from "../../PromoFittedText.vue";

import {
  composeMarket,
  composeListMarketOptions,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";

import { getMarketOptionText } from "../../../lib/language";
import { Market, SportEvent, Option } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    padding: "0px 5px 3px 5px",
    height: "inherit",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`
  },
  listCardWrapper: {
    display: "grid",
    gridTemplateColumns: "4fr 1fr",
    height: "100%",
    justifyContent: "space-between",
    boxSizing: "border-box",
    gap: "4px"
  },
  promoWrapper: {
    flex: "1 1 0",
    minHeight: "0px",
    overflow: "hidden",
    boxSizing: "border-box",
    flexDirection: "column",
    display: "flex"
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "208px"
  },
  eventName: {
    display: "flex",
    boxSizing: "border-box",
    "&.event-name": {
      justifyContent: "flex-start",
      alignItems: "center"
    },
    "& div, & span": {
      fontSize: "9px",
      textAlign: "left"
    }
  },

  optionButton: {
    boxSizing: "border-box",
    justifyContent: "center !important",
    width: "20%",
    minWidth: "64px",
    "& > a": {
      justifyContent: "center"
    },
    "& .name": {
      display: "none"
    },
    "& .name + div": {
      margin: "0"
    }
  }
});

export default defineComponent({
  name: "MobileLeaderboard",
  components: {
    CardHeader,
    ListOption,
    EventName,
    PromoFittedText
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const {
      adTemplateBranding,
      marketBranding
    } = injectBrand() as BrandContext;
    const classesRef = useStyles(adTemplateBranding);
    const market = composeMarket(props.marketId) as ComputedRef<Market>;

    const marketOptions = composeListMarketOptions(
      props.marketId
    ) as ComputedRef<Option[]>;

    const marketOption = computed(() => marketOptions.value[0]);
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;

    const optionText = getMarketOptionText(
      market.value,
      event.value,
      marketOption.value
    ) as ComputedRef<string>;

    return {
      optionText,
      market,
      marketBranding,
      classes: classesRef,
      option: marketOption,
      event,
      marketNameStyle: {
        get(brand: Brand) {
          return brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
